import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Pagination,
  Radio,
  Stack,
  Typography
} from '@mui/material'
import PaymentIcon from '@mui/icons-material/Payment'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import { Currency } from 'react-intl-number-format'
import { useNavigate, useSearchParams } from 'react-router-dom'

import ShopCard from '../components/Items/ShopCard'
import BankTransferDetailsCard from '../components/Orders/BankTransferDetailsCard'
import { StyledBox } from '../components/styledComponents/StyledItemComponents'
import { useAppContext } from '../context/AppProvider'
import { useCart } from '../hooks/useCart'
import { theme } from '../theme'
import { getBankTransferDetailsFromSettings } from '../utils/bankTransfer'
import { getAvailabilityBreakdown, hasOnOrderQuantity } from '../utils/itemPresentation'
import { downloadCartOfferPdf } from '../utils/orderDocument'
import {
  buildDeliveryAddressSummary,
  buildDeliveryProfileValidationMessage,
  getDefaultDeliveryProfileId,
  getOrderedDeliveryProfiles,
  normalizeManagedDeliveryProfiles,
  normalizeSavedDeliveryProfiles
} from '../utils/deliveryProfiles'

const getDeliveryModeButtonSx = (active) => ({
  minHeight: '50px',
  height: '50px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '16px',
  borderWidth: '2px',
  borderStyle: 'solid',
  borderColor: active ? theme.palette.accent.main : theme.palette.borderColor.main,
  backgroundColor: active ? theme.palette.accent.main : theme.palette.primary.main,
  color: active ? theme.palette.primary.main : theme.palette.fontColor.main,
  textTransform: 'none',
  fontWeight: 700,
  fontSize: { xs: '12px', sm: '13px' },
  lineHeight: 1.4,
  boxShadow: active ? '0 8px 18px rgba(255, 0, 21, 0.16)' : 'none',
  '&:hover': {
    backgroundColor: active ? theme.palette.accent.light : theme.palette.secondary.main,
    borderColor: active ? theme.palette.accent.light : theme.palette.fontColor.medium,
    boxShadow: active ? '0 8px 18px rgba(255, 0, 21, 0.2)' : 'none'
  }
})

const Cosulmeu = () => {
  const navigate = useNavigate()
  const { state } = useAppContext()
  const { getCartItems, sendOrder, getCartOffer, resetCartState } = useCart()
  const [pageNumber, setPage] = useState(1)
  const [searchParams, setSearchParams] = useSearchParams()
  const [orderResponse, setOrderResponse] = useState({ message: '', type: '' })
  const [submitting, setSubmitting] = useState(false)
  const [downloadingOffer, setDownloadingOffer] = useState(false)
  const submitLockRef = useRef(false)

  const userData = state.user.data || {}
  const companyProfile = userData.company_profile || {}
  const managedDeliveryProfiles = useMemo(() => normalizeManagedDeliveryProfiles(userData), [userData])
  const savedDeliveryProfiles = useMemo(() => normalizeSavedDeliveryProfiles(userData), [userData])
  const invalidSavedDeliveryProfiles = useMemo(
    () => managedDeliveryProfiles.filter(profile => !profile.is_valid),
    [managedDeliveryProfiles]
  )
  const activeUserId = userData._id || ''
  const defaultSavedDeliveryProfileId = useMemo(
    () => getDefaultDeliveryProfileId(userData, savedDeliveryProfiles),
    [savedDeliveryProfiles, userData]
  )
  const hasSavedDeliveryAddress = savedDeliveryProfiles.length > 0
  const hasInvalidSavedAddresses = invalidSavedDeliveryProfiles.length > 0
  const deliveryUserRef = useRef(activeUserId)
  const [deliveryMode, setDeliveryMode] = useState('billing')
  const [paymentMethod, setPaymentMethod] = useState('card')
  const [selectedSavedAddressId, setSelectedSavedAddressId] = useState(defaultSavedDeliveryProfileId || '')
  const selectedSavedDeliveryProfile = useMemo(
    () => savedDeliveryProfiles.find(profile => profile.id === selectedSavedAddressId) || null,
    [savedDeliveryProfiles, selectedSavedAddressId]
  )
  const orderedSavedDeliveryProfiles = useMemo(
    () => getOrderedDeliveryProfiles(savedDeliveryProfiles, selectedSavedAddressId),
    [savedDeliveryProfiles, selectedSavedAddressId]
  )
  const bankTransferDetails = useMemo(
    () => getBankTransferDetailsFromSettings(state.settings || {}),
    [state.settings]
  )
  const onOrderCartItems = useMemo(
    () => (state.cart.results || [])
      .filter(item => hasOnOrderQuantity(item, item.count))
      .map(item => ({
        item,
        availability: getAvailabilityBreakdown(item, item.count)
      })),
    [state.cart.results]
  )

  useEffect(() => {
    const userChanged = deliveryUserRef.current !== activeUserId
    if (userChanged) {
      deliveryUserRef.current = activeUserId
      setDeliveryMode('billing')
      setSelectedSavedAddressId(defaultSavedDeliveryProfileId || '')
      return
    }

    setSelectedSavedAddressId((currentId) => {
      if (currentId && savedDeliveryProfiles.some(profile => profile.id === currentId)) {
        return currentId
      }
      return defaultSavedDeliveryProfileId || ''
    })
  }, [activeUserId, defaultSavedDeliveryProfileId, savedDeliveryProfiles])

  useEffect(() => {
    if (paymentMethod === 'bank_transfer' && !bankTransferDetails.configured) {
      setPaymentMethod('card')
    }
  }, [bankTransferDetails.configured, paymentMethod])

  const maxResult = 24
  const totalPages = state.cart.total_unique_items ? Math.ceil(state.cart.total_unique_items / maxResult) : 0

  useEffect(() => {
    setPage(pageNumber)
    getCartItems(pageNumber)
  }, [getCartItems, pageNumber])

  const paginationHandleChange = (_event, value) => {
    if (value !== pageNumber) {
      const updatedFilters = { ...Object.fromEntries(searchParams.entries()), page: value.toString() }
      setPage(value)
      getCartItems(value)
      setSearchParams(updatedFilters)
    }
  }

  const billingSummary = useMemo(() => ({
    company: userData.company || companyProfile.legal_name || 'Compania dvs.',
    cui: userData.cui || '-',
    registration_no: companyProfile.registration_no || '-',
    address: companyProfile.fiscal_address || 'Adresa firmei nu este completată încă.'
  }), [userData.company, userData.cui, companyProfile.legal_name, companyProfile.registration_no, companyProfile.fiscal_address])

  const activeDeliveryAddress = useMemo(() => {
    if (deliveryMode === 'billing') {
      return billingSummary.address
    }

    if (selectedSavedDeliveryProfile) {
      return buildDeliveryAddressSummary(selectedSavedDeliveryProfile)
    }
    return ''
  }, [billingSummary.address, deliveryMode, selectedSavedDeliveryProfile])

  const validateDelivery = () => {
    if (deliveryMode === 'billing') {
      return null
    }
    if (selectedSavedDeliveryProfile) {
      return null
    }
    if (hasInvalidSavedAddresses && !hasSavedDeliveryAddress) {
      return 'Ai adrese salvate incomplete. Completează-le în profil înainte de a plasa comanda.'
    }
    return 'Selectează o adresă de livrare salvată sau gestionează adresele din profil.'
  }

  const handleDeliveryModeChange = (mode) => {
    setDeliveryMode(mode)
    if (mode === 'custom' && !selectedSavedAddressId) {
      setSelectedSavedAddressId(defaultSavedDeliveryProfileId || savedDeliveryProfiles[0]?.id || '')
    }
  }

  const handleSendOrder = async () => {
    if (submitLockRef.current) {
      return
    }

    setOrderResponse({ message: '', type: '' })
    const deliveryError = validateDelivery()
    if (deliveryError) {
      setOrderResponse({ message: deliveryError, type: 'error' })
      return
    }
    if (paymentMethod === 'bank_transfer' && !bankTransferDetails.configured) {
      setOrderResponse({ message: 'Metoda Ordin de plata nu este disponibila momentan.', type: 'error' })
      return
    }

    submitLockRef.current = true
    setSubmitting(true)
    const deliveryPayload = deliveryMode === 'billing'
      ? {
          same_as_billing: true
        }
      : {
          same_as_billing: false,
          delivery_profile_id: selectedSavedAddressId
        }
    const response = await sendOrder({
      payment_method: paymentMethod,
      delivery: deliveryPayload
    })
    if (response.statusCode === 200 && response.checkoutUrl) {
      setOrderResponse({ message: 'Redirecționare către pagina securizată de plată…', type: 'success' })
      window.location.assign(response.checkoutUrl)
      return
    }
    if (response.statusCode === 200 && response.orderId) {
      if (paymentMethod === 'bank_transfer') {
        await resetCartState()
      }
      setOrderResponse({
        message: response.message || (paymentMethod === 'bank_transfer'
          ? 'Comanda a fost înregistrată. Te redirecționăm către sumarul comenzii.'
          : 'Comanda a fost înregistrată.'),
        type: 'success'
      })
      const params = new URLSearchParams({
        order: response.orderId,
        order_number: response.orderNumber || response.order?.order_number || ''
      })
      window.location.assign(`/checkout/success?${params.toString()}`)
      return
    }
    if (response.statusCode === 200 && response.order?.payment_status === 'paid' && response.orderId) {
      setOrderResponse({ message: 'Plata acestei comenzi a fost deja confirmată. Redirecționăm către sumarul comenzii…', type: 'success' })
      const params = new URLSearchParams({
        order: response.orderId,
        order_number: response.orderNumber || response.order?.order_number || ''
      })
      window.location.assign(`/checkout/success?${params.toString()}`)
      return
    }
    setOrderResponse({
      message: response.message || 'Comanda nu a putut fi inițiată. Încercați din nou.',
      type: 'error'
    })
    setSubmitting(false)
    submitLockRef.current = false
  }

  const handleDownloadOffer = async () => {
    try {
      setDownloadingOffer(true)
      const payload = await getCartOffer({
        delivery: deliveryMode === 'billing'
          ? { same_as_billing: true }
          : { same_as_billing: false, delivery_profile_id: selectedSavedAddressId }
      })
      await downloadCartOfferPdf(payload.data || payload.offer || payload)
    } catch (offerError) {
      setOrderResponse({
        message: offerError.message || 'Oferta nu a putut fi generată acum.',
        type: 'error'
      })
    } finally {
      setDownloadingOffer(false)
    }
  }

  return (
    <>
      {orderResponse.message && (
        <Box sx={{ padding: '20px 0' }}>
          <Typography
            variant='body1'
            sx={{
              color: orderResponse.type === 'success' ? 'green' : 'red',
              textAlign: 'center'
            }}
          >
            {orderResponse.message}
          </Typography>
        </Box>
      )}
      <Box sx={{ backgroundColor: theme.palette.secondary.main, padding: '40px 0' }}>
        <StyledBox>
          <Stack bgcolor={theme.palette.primary.main} p='20px 30px' border='solid 1px' borderColor={theme.palette.borderColor.main} borderRadius='4px' spacing={4}>
            <Typography variant='span' fontSize='18px' fontWeight={600}>Coșul meu</Typography>
            <Divider orientation='horizontal' aria-hidden='true' color={theme.palette.fontColor.main} />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} flexWrap='wrap'>
              <Chip
                label={`Produse distincte: ${state.cart.total_unique_items || 0}`}
                sx={{
                  alignSelf: 'flex-start',
                  fontWeight: 700,
                  backgroundColor: theme.palette.secondary.main,
                  border: `1px solid ${theme.palette.borderColor.main}`
                }}
              />
              <Chip
                label={`Bucati in cos: ${state.cart.total_items || 0}`}
                sx={{
                  alignSelf: 'flex-start',
                  fontWeight: 700,
                  color: theme.palette.primary.main,
                  backgroundColor: theme.palette.accent.main
                }}
              />
            </Stack>
            {onOrderCartItems.length > 0 && (
              <Alert severity='warning' sx={{ '& .MuiAlert-message': { fontSize: '12px', lineHeight: 1.75 } }}>
                <Stack spacing={0.75}>
                  <Typography sx={{ fontSize: '12px', fontWeight: 700 }}>
                    Unele produse depasesc stocul disponibil. Plata se poate face integral, iar bucatile la comanda pot avea termen de livrare mai mare.
                  </Typography>
                  <Stack component='ul' spacing={0.35} sx={{ pl: 2, m: 0 }}>
                    {onOrderCartItems.map(({ item, availability }) => (
                      <Typography component='li' key={item._id} sx={{ fontSize: '11.5px', lineHeight: 1.7 }}>
                        {item.name}: {availability.inStockQuantity} in stoc, {availability.onOrderQuantity} la comanda
                      </Typography>
                    ))}
                  </Stack>
                </Stack>
              </Alert>
            )}

            <Stack direction={{ xs: 'column', xl: 'row' }} spacing={4} alignItems='flex-start'>
              <Box sx={{ flex: 1, width: '100%' }}>
                <Stack spacing={2}>
                  {state.cart.results?.map((item) => (
                    <ShopCard key={item._id} item={item} showShipping={false} />
                  ))}
                  <Box sx={{ display: 'flex', justifyContent: 'center' }} padding='25px 0'>
                    <Pagination count={totalPages} page={pageNumber} onChange={paginationHandleChange} />
                  </Box>
                </Stack>
              </Box>

              <Stack spacing={3} sx={{ width: { xs: '100%', xl: 380 }, flexShrink: 0 }}>
                <Box sx={{ border: '1px solid', borderColor: theme.palette.borderColor.main, borderRadius: '12px', p: 2 }}>
                  <Typography variant='h6' sx={{ fontSize: '12px', marginBottom: '8px' }}>Facturare firmă</Typography>
                  <Typography variant='body2' sx={{ fontSize: '11px', lineHeight: 1.8 }}><strong>Companie:</strong> {billingSummary.company}</Typography>
                  <Typography variant='body2' sx={{ fontSize: '11px', lineHeight: 1.8 }}><strong>CUI:</strong> {billingSummary.cui}</Typography>
                  <Typography variant='body2' sx={{ fontSize: '11px', lineHeight: 1.8 }}><strong>Nr. Reg. Com.:</strong> {billingSummary.registration_no}</Typography>
                  <Typography variant='body2' sx={{ fontSize: '11px', lineHeight: 1.8 }}><strong>Adresă:</strong> {billingSummary.address}</Typography>
                </Box>

                <Box sx={{ border: '1px solid', borderColor: theme.palette.borderColor.main, borderRadius: '12px', p: 2 }}>
                  <Typography variant='h6' sx={{ fontSize: '12px', marginBottom: '8px' }}>Livrare</Typography>
                  <Stack spacing={1.5}>
                    <Typography variant='body2' sx={{ color: theme.palette.fontColor.medium, fontSize: '11px', lineHeight: 1.8 }}>
                      Alege dacă livrarea se face la adresa firmei sau la o altă adresă.
                    </Typography>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                        gap: 1.5
                      }}
                    >
                      <Box>
                        <Button
                          fullWidth
                          onClick={() => handleDeliveryModeChange('billing')}
                          sx={getDeliveryModeButtonSx(deliveryMode === 'billing')}
                        >
                          La adresa firmei
                        </Button>
                      </Box>
                      <Box>
                        <Button
                          fullWidth
                          onClick={() => handleDeliveryModeChange('custom')}
                          sx={getDeliveryModeButtonSx(deliveryMode === 'custom')}
                        >
                          La altă adresă
                        </Button>
                      </Box>
                    </Box>

                    {deliveryMode === 'billing' && (
                      <Box
                        sx={{
                          border: '1px solid',
                          borderColor: theme.palette.accent.main,
                          borderRadius: '12px',
                          p: 1.5,
                          backgroundColor: '#fff6f7'
                        }}
                      >
                        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: theme.palette.fontColor.main, marginBottom: '6px' }}>
                          Livrare la adresa firmei
                        </Typography>
                        <Typography variant='body2' sx={{ color: theme.palette.fontColor.medium, fontSize: '11px', lineHeight: 1.8 }}>
                          Adresa folosită pentru livrare: {activeDeliveryAddress || 'Adresa firmei nu este completată încă.'}
                        </Typography>
                      </Box>
                    )}

                  </Stack>

                  {deliveryMode === 'custom' && (
                    <Stack spacing={2} mt={1}>
                      <Alert severity='info' sx={{ '& .MuiAlert-message': { fontSize: '11px', lineHeight: 1.8 } }}>
                        Alege o adresă salvată pentru livrare. Adresele alternative se gestionează din profil și nu se mai modifică direct în checkout.
                      </Alert>

                      {hasInvalidSavedAddresses && (
                        <Alert severity='warning' sx={{ '& .MuiAlert-message': { fontSize: '11px', lineHeight: 1.8 } }}>
                          Unele adrese salvate sunt incomplete și nu pot fi folosite la checkout. {buildDeliveryProfileValidationMessage(invalidSavedDeliveryProfiles[0].missing_required_fields)}
                        </Alert>
                      )}

                      {!selectedSavedDeliveryProfile && hasSavedDeliveryAddress && (
                        <Alert severity='warning' sx={{ '& .MuiAlert-message': { fontSize: '11px', lineHeight: 1.8 } }}>
                          Selectează o adresă salvată validă înainte să plasezi comanda.
                        </Alert>
                      )}

                      {hasSavedDeliveryAddress && (
                        <Stack spacing={1.5}>
                          <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1.5}
                            justifyContent='space-between'
                            alignItems={{ xs: 'stretch', sm: 'center' }}
                          >
                            <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>
                              Adrese salvate
                            </Typography>
                            <Button
                              variant='outlined'
                              color='accent'
                              onClick={() => navigate('/profilulmeu#DeliveryAddresses')}
                              sx={{ alignSelf: { xs: 'stretch', sm: 'auto' }, textTransform: 'none', borderRadius: '12px' }}
                            >
                              Gestionează adresele în profil
                            </Button>
                          </Stack>
                          <Box
                            data-testid='saved-addresses-scroll'
                            sx={{
                              maxHeight: { xs: 280, md: 320 },
                              overflowY: 'auto',
                              pr: 0.5,
                              display: 'grid',
                              gap: 1.25,
                              scrollbarWidth: 'thin',
                              '&::-webkit-scrollbar': {
                                width: '8px'
                              },
                              '&::-webkit-scrollbar-thumb': {
                                backgroundColor: theme.palette.borderColor.main,
                                borderRadius: '999px'
                              }
                            }}
                          >
                          {orderedSavedDeliveryProfiles.map((profile) => {
                            const isSelected = profile.id === selectedSavedAddressId
                            const isDefault = profile.id === defaultSavedDeliveryProfileId
                            return (
                              <Box
                                key={profile.id}
                                onClick={() => setSelectedSavedAddressId(profile.id)}
                                sx={{
                                  border: '1px solid',
                                  borderColor: isSelected ? theme.palette.accent.main : theme.palette.borderColor.main,
                                  borderRadius: '12px',
                                  padding: '10px 12px',
                                  cursor: 'pointer',
                                  backgroundColor: isSelected ? '#fff6f7' : theme.palette.primary.main
                                }}
                              >
                                <Stack direction='row' spacing={1.5} alignItems='flex-start'>
                                  <Radio
                                    checked={isSelected}
                                    onChange={() => setSelectedSavedAddressId(profile.id)}
                                    value={profile.id}
                                    sx={{
                                      color: theme.palette.fontColor.main,
                                      '&.Mui-checked': { color: theme.palette.accent.main }
                                    }}
                                  />
                                  <Box sx={{ minWidth: 0 }}>
                                    <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap'>
                                      <Typography sx={{ fontSize: '12px', fontWeight: 700, lineHeight: 1.55 }}>
                                        {profile.display_name || profile.recipient_name || 'Adresă salvată'}
                                      </Typography>
                                      {isDefault && (
                                        <Chip size='small' color='error' label='Implicită' />
                                      )}
                                    </Stack>
                                    {profile.company && (
                                      <Typography variant='body2' sx={{ color: theme.palette.fontColor.medium, fontSize: '10.75px', lineHeight: 1.8 }}>
                                        {profile.company}
                                      </Typography>
                                    )}
                                    <Typography variant='body2' sx={{ color: theme.palette.fontColor.medium, fontSize: '10.75px', lineHeight: 1.8 }}>
                                      {buildDeliveryAddressSummary(profile)}
                                    </Typography>
                                    {profile.recipient_phone && (
                                      <Typography variant='body2' sx={{ color: theme.palette.fontColor.medium, fontSize: '10.75px', lineHeight: 1.8 }}>
                                        {profile.recipient_phone}
                                      </Typography>
                                    )}
                                  </Box>
                                </Stack>
                              </Box>
                            )
                          })}
                          </Box>
                        </Stack>
                      )}
                      {!hasSavedDeliveryAddress && (
                        <Stack spacing={2}>
                          <Alert severity='warning' sx={{ '& .MuiAlert-message': { fontSize: '11.5px', lineHeight: 1.75 } }}>
                            {hasInvalidSavedAddresses
                              ? 'Nu ai încă nicio adresă alternativă completă pentru checkout. Completează una din profil și încearcă din nou.'
                              : 'Nu ai adrese alternative salvate încă. Adaugă una din profil pentru a o folosi la checkout.'}
                          </Alert>
                          <Button
                            variant='outlined'
                            color='accent'
                            onClick={() => navigate('/profilulmeu#DeliveryAddresses')}
                            sx={{ alignSelf: 'flex-start', textTransform: 'none', borderRadius: '12px' }}
                          >
                            Gestionează adresele în profil
                          </Button>
                        </Stack>
                      )}
                    </Stack>
                  )}
                </Box>

                <Box sx={{ border: '1px solid', borderColor: theme.palette.borderColor.main, borderRadius: '12px', p: 2 }}>
                  <Typography variant='h6' sx={{ fontSize: '12px', marginBottom: '8px' }}>Metoda de plata</Typography>
                  <Stack spacing={1.5}>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                        gap: 1.5
                      }}
                    >
                      <Button
                        fullWidth
                        onClick={() => setPaymentMethod('card')}
                        sx={getDeliveryModeButtonSx(paymentMethod === 'card')}
                      >
                        Card online
                      </Button>
                      <Button
                        fullWidth
                        onClick={() => setPaymentMethod('bank_transfer')}
                        disabled={!bankTransferDetails.configured}
                        sx={getDeliveryModeButtonSx(paymentMethod === 'bank_transfer')}
                      >
                        Ordin de plata
                      </Button>
                    </Box>
                    <Alert severity='info' sx={{ '& .MuiAlert-message': { fontSize: '11px', lineHeight: 1.8 } }}>
                      {paymentMethod === 'card'
                        ? 'Comanda se inregistreaza acum, iar plata continua imediat pe pagina securizata Stripe.'
                        : 'Comanda se inregistreaza imediat, cosul se goleste, descarci proforma, faci transferul in contul afisat mai jos si apoi incarci dovada platii pentru confirmare.'}
                    </Alert>
                    {!bankTransferDetails.configured && (
                      <Alert severity='warning' sx={{ '& .MuiAlert-message': { fontSize: '11px', lineHeight: 1.8 } }}>
                        Ordinul de plata nu este disponibil momentan deoarece detaliile bancare nu sunt configurate complet.
                      </Alert>
                    )}
                    {paymentMethod === 'bank_transfer' && bankTransferDetails.configured && (
                      <BankTransferDetailsCard
                        details={bankTransferDetails}
                        title='Date bancare pentru ordinul de plata'
                        helperText='Contul bancar poate fi verificat acum. Numarul comenzii se genereaza dupa plasare si il folosesti apoi ca referinta a platii.'
                      />
                    )}
                  </Stack>
                </Box>

                <Box sx={{ border: '1px solid', borderColor: theme.palette.borderColor.main, borderRadius: '12px', p: 2 }}>
                  <Typography variant='h6' sx={{ fontSize: '13px', marginBottom: '12px' }}>Sumar comandă</Typography>
                  <Typography component='div' variant='body2' sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '11.5px', lineHeight: 1.75 }}>
                    <span>Subtotal</span>
                    <span><Currency locale='ro-RO' currency='RON'>{state.cart.subtotal_price}</Currency></span>
                  </Typography>
                  <Typography component='div' variant='body2' sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '11.5px', lineHeight: 1.75 }}>
                    <span>Transport</span>
                    <span><Currency locale='ro-RO' currency='RON'>{state.cart.shipping_price}</Currency></span>
                  </Typography>
                  <Typography component='div' variant='body1' sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: '12.5px', lineHeight: 1.7 }}>
                    <span>Total</span>
                    <span><Currency locale='ro-RO' currency='RON'>{state.cart.total_price}</Currency></span>
                  </Typography>
                  <Button
                    variant='contained'
                    size='large'
                    onClick={handleSendOrder}
                    disabled={submitting || !state.cart.results || state.cart.results.length === 0}
                    color='accent'
                    endIcon={<PaymentIcon />}
                    sx={{ color: 'white', borderRadius: '18px', width: '100%', marginTop: '18px', textTransform: 'none' }}
                  >
                    Plasează comanda
                  </Button>
                  <Button
                    variant='outlined'
                    size='large'
                    onClick={handleDownloadOffer}
                    disabled={downloadingOffer || !state.cart.results || state.cart.results.length === 0}
                    color='accent'
                    startIcon={<DownloadOutlinedIcon />}
                    sx={{ borderRadius: '18px', width: '100%', marginTop: '10px', textTransform: 'none' }}
                  >
                    {downloadingOffer ? 'Generam oferta...' : 'Descarca oferta PDF'}
                  </Button>
                  <Alert severity='info' sx={{ marginTop: '14px', '& .MuiAlert-message': { fontSize: '11.5px', lineHeight: 1.75 } }}>
                    {paymentMethod === 'card'
                      ? 'Comanda se inregistreaza acum, iar plata continua imediat pe pagina securizata. Cosul se goleste doar dupa confirmarea platii.'
                      : 'Comanda se inregistreaza acum, cosul se goleste imediat, proforma ramane disponibila in cont, transferul se face in contul afisat si apoi poti incarca dovada platii.'}
                  </Alert>
                </Box>
              </Stack>
            </Stack>
          </Stack>
        </StyledBox>
      </Box>
    </>
  )
}

export default Cosulmeu
